import React, { useEffect, useState } from 'react';

interface LogoPropsI {
    isLoading: boolean;
    isMobileView?: boolean;
}

/**
 * Core brand mark — geometric "AH" lettermark
 * Space Grotesk 700 for authority. Clean, no period. Copper underline accent.
 */
export const BrandMark: React.FC<{ size?: number }> = ({ size = 40 }) => {
    const scale = size / 40;
    return (
        <div style={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: `${3 * scale}px`,
            userSelect: 'none',
        }}>
            <span style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: `${22 * scale}px`,
                color: '#F5F5F5',
                letterSpacing: `${-1 * scale}px`,
                lineHeight: 1,
            }}>AH</span>
            {/* Copper underline accent */}
            <div style={{
                width: `${16 * scale}px`,
                height: `${2 * scale}px`,
                background: '#C4956A',
                borderRadius: `${1 * scale}px`,
            }} />
        </div>
    );
};

/**
 * Cinematic loading sequence — Founder brand reveal:
 *  Phase 1: Thin copper line draws from center
 *  Phase 2: "A" fades up clean
 *  Phase 3: "H" fades up (staggered)
 *  Phase 4: Copper underline draws beneath
 *  Phase 5: Subtle warm glow
 *  Phase 6: Exit — scale up + dissolve
 */
const CinematicLoadingSequence: React.FC = () => {
    const [phase, setPhase] = useState(0);

    useEffect(() => {
        const timers = [
            setTimeout(() => setPhase(1), 80),
            setTimeout(() => setPhase(2), 500),
            setTimeout(() => setPhase(3), 700),
            setTimeout(() => setPhase(4), 950),
            setTimeout(() => setPhase(5), 1200),
            setTimeout(() => setPhase(6), 1800),
        ];
        return () => timers.forEach(clearTimeout);
    }, []);

    const isExiting = phase >= 6;

    const letterBase: React.CSSProperties = {
        fontFamily: "'Space Grotesk', sans-serif",
        fontWeight: 700,
        fontSize: '80px',
        lineHeight: 1,
        display: 'inline-block',
        transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: '#0A0A0A',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isExiting ? 0 : 1,
            transition: 'opacity 0.6s cubic-bezier(0.7, 0, 0.3, 1)',
            pointerEvents: 'none',
            overflow: 'hidden',
        }}>
            {/* Subtle warm glow behind text */}
            <div style={{
                position: 'absolute',
                width: '300px',
                height: '300px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(196, 149, 106, 0.06) 0%, transparent 70%)',
                opacity: phase >= 5 ? 1 : 0,
                transition: 'opacity 0.8s ease',
                pointerEvents: 'none',
            }} />

            {/* Left decorative line — copper */}
            <div style={{
                position: 'absolute',
                top: '50%',
                right: '50%',
                marginRight: '70px',
                height: '1px',
                width: phase >= 1 ? '80px' : '0px',
                background: 'linear-gradient(to left, rgba(196, 149, 106, 0.4), transparent)',
                transition: `width 0.7s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.5s ease`,
                opacity: phase >= 5 ? 0 : 1,
            }} />

            {/* Right decorative line — copper */}
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginLeft: '70px',
                height: '1px',
                width: phase >= 1 ? '80px' : '0px',
                background: 'linear-gradient(to right, rgba(196, 149, 106, 0.4), transparent)',
                transition: `width 0.7s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.5s ease`,
                opacity: phase >= 5 ? 0 : 1,
            }} />

            {/* Main monogram + underline */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                userSelect: 'none',
                transform: isExiting ? 'scale(1.05)' : 'scale(1)',
                transition: 'transform 0.6s cubic-bezier(0.7, 0, 0.3, 1)',
            }}>
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                    {/* Letter A */}
                    <span style={{
                        ...letterBase,
                        color: '#F5F5F5',
                        opacity: phase >= 2 ? 1 : 0,
                        transform: phase >= 2 ? 'translateY(0)' : 'translateY(20px)',
                        filter: phase >= 2 ? 'blur(0px)' : 'blur(6px)',
                        letterSpacing: '-3px',
                    }}>A</span>

                    {/* Letter H */}
                    <span style={{
                        ...letterBase,
                        color: '#F5F5F5',
                        opacity: phase >= 3 ? 1 : 0,
                        transform: phase >= 3 ? 'translateY(0)' : 'translateY(20px)',
                        filter: phase >= 3 ? 'blur(0px)' : 'blur(6px)',
                        letterSpacing: '-3px',
                    }}>H</span>
                </div>

                {/* Copper underline — draws in */}
                <div style={{
                    width: phase >= 4 ? '40px' : '0px',
                    height: '3px',
                    background: '#C4956A',
                    borderRadius: '2px',
                    transition: 'width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                }} />
            </div>
        </div>
    );
};

export const Logo: React.FC<LogoPropsI> = ({ isLoading, isMobileView }) => {
    if (isLoading) {
        return <CinematicLoadingSequence />;
    }

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                cursor: 'pointer',
                transition: 'opacity 0.3s ease',
            }}
        >
            <BrandMark size={36} />

            {!isMobileView && (
                <span
                    style={{
                        color: '#F5F5F5',
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 500,
                        fontSize: '15px',
                        letterSpacing: '0.01em',
                        transition: 'opacity 0.3s ease',
                    }}
                >
                    Asim Hafeez
                </span>
            )}
        </div>
    );
};
